import { useEffect, useState, useCallback } from 'react';
import { useCheckout } from '@/contexts/checkoutContext';
import { useShipping } from '@/contexts/shippingContext';
import { useSetDeliveryMethod } from '@/hooks/useSetDeliveryMethod';
import type { StoreLocations } from '@/types';

const MAX_STORE_LOCATIONS = 3;

export const useClickAndCollect = () => {
  const {
    checkoutData: { clickAndCollectLocations },
  } = useCheckout();
  const { setClickAndCollectStoreId: setStoreId } = useShipping();
  const { setShippingMethod } = useSetDeliveryMethod();

  const [filter, setFilter] = useState('');
  const [stores, setStores] = useState<StoreLocations[]>([]);

  // Wrap setLocation to also call SetShippingMethod
  const setLocation = useCallback((storeId: string) => {
    // Set the selected store ID
    setStoreId(storeId);
    
    // Also set the shipping method for Click & Collect
    setShippingMethod({ shippingMethodName: 'collect' });
  }, [setStoreId, setShippingMethod]);

  useEffect(() => {
    const storesPropsOmitted = clickAndCollectLocations?.map(
      ({ city, company, county, postalCode }) => ({
        city,
        company,
        county,
        postalCode,
      }),
    );

    const filteredStores = storesPropsOmitted?.filter(store => {
      const storeValues = Object.values(store).map(value =>
        value?.toLowerCase(),
      );

      for (const value of storeValues) {
        if (
          filter &&
          typeof value !== 'undefined' &&
          value.indexOf(filter.toLowerCase()) > -1
        ) {
          return true;
        }
      }
    });

    const topStoreResults =
      filteredStores
        ?.filter((_, idx) => idx < MAX_STORE_LOCATIONS)
        .map(store =>
          clickAndCollectLocations?.find(
            location => location.company === store.company,
          ),
        )
        .filter(store => typeof store !== 'undefined') ?? [];

    setStores(topStoreResults);
  }, [clickAndCollectLocations, filter]);

  return { filter, setFilter, setLocation, stores };
};
